// libs
import { makeStyles } from '@material-ui/styles'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import Alert from 'core/components/Alert'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
// Components
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import StatusBar, { getClusterUpgradeTitle } from 'core/components/status-bar/StatusBar'
import Text from 'core/elements/Text'
import useClusterChangeReload from 'core/hooks/useClusterChangeReload'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import ClusterUpgradeModal from 'k8s/components/kaapi/cluster-upgrade/ClusterUpgradeModal'
import {
  ClusterUpgradeSteps,
  clusterUpgradeStatusMap,
} from 'k8s/components/kaapi/cluster-upgrade/model'
import { formatDate } from 'utils/misc'
import { deleteKaapiCluster, listKaapiClusters } from '../../components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../../components/kaapi/clusters/selectors'
import { getClusterHealth } from '../kaapi/clusters/clusterHealth'
import { isUnhealthy, machineHealthSeverityRank } from '../kaapi/machines/machineHealth'
import { kaapiMachinesForClusterSelector } from '../kaapi/machines/selectors'
import { listByoHosts } from '../nodes/byoh/actions'
import ClusterOverview from './ClusterOverview'
import { isTimeExpired } from './helpers'
import type { IClusterStatus } from './model'
import ResourceUtilizationCard from './ResourceUtilizationCard'
import { getUnhealthyMachinesBannerTitle } from './unhealthyMachinesBanner'

const ClusterDetails = () => {
  const classes = useStyles({})
  const { history } = useReactRouter()
  const { prefs: k8sPluginGlobalPerfs, updatePrefs } = useScopedPreferences('k8sPluginGlobalParams')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Use clusters loaded by parent (ClusterDetailTabs) - loadOnDemand prevents auto-load
  const { loading, reload } = useListAction(listKaapiClusters, { loadOnDemand: true })
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )
  const clusterUpgradeJob = (cluster?.upgradeJobs || []).find(
    (job) => job.clusterName === cluster.metadata.name,
  )
  // BYOH install/join failures are reported only on the ByoHost, which the
  // machine health classifier reads to tell a failed join from an unclaimed host.
  // Gated so an OpenStack-only cluster never issues the byohosts/byomachines
  // requests - the CRDs need not even be installed there.
  useListAction(listByoHosts, { preRequisites: !!cluster?.isBYOHCluster })
  const machinesSelector = kaapiMachinesForClusterSelector({
    clusterName: cluster?.metadata?.name,
  })
  const machines = useSelector(machinesSelector) || []
  const unhealthyMachines = useMemo(
    () =>
      machines
        .filter((m) => isUnhealthy(m?.machineHealth))
        .sort((a, b) => {
          const severityDiff =
            (machineHealthSeverityRank[b?.machineHealth?.state] || 0) -
            (machineHealthSeverityRank[a?.machineHealth?.state] || 0)
          if (severityDiff !== 0) return severityDiff
          // Tie-breaker: Master nodes rank before Worker nodes at equal severity
          if (a?.nodeType === 'Master' && b?.nodeType !== 'Master') return -1
          if (a?.nodeType !== 'Master' && b?.nodeType === 'Master') return 1
          return 0
        }),
    [machines],
  )

  const clusterHealth = useMemo(
    () => getClusterHealth(cluster, undefined, cluster?.infraFailureEvent),
    [cluster],
  )

  // Delete cluster action
  const { update: deleteKaapiClusterFn, updating: deletingKaapiCluster } =
    useUpdateAction(deleteKaapiCluster)

  // Reload only when cluster actually changes after component mount (not on first mount)
  useClusterChangeReload(k8sPluginGlobalPerfs?.cluster, reload)

  const deleteCluster = async () => {
    setShowDeleteDialog(false)
    await deleteKaapiClusterFn({ name: cluster?.metadata?.name })
    reload(true, false)
  }

  const completionTime = clusterUpgradeJob?.completionTime
    ? formatDate(clusterUpgradeJob?.completionTime)
    : '-'
  const expectedCompletionTime = clusterUpgradeJob?.expectedCompletionTime
    ? formatDate(clusterUpgradeJob?.expectedCompletionTime)
    : '-'

  const statusDetails = {
    status: (clusterUpgradeStatusMap[clusterUpgradeJob?.phase] as IClusterStatus) || 'loading',
    title: getClusterUpgradeTitle(clusterUpgradeJob?.phase),
    error: clusterUpgradeJob,
    subtitles: [
      {
        label: 'Target K8s Version',
        value: (
          <div className={classes.currentVersion}>{clusterUpgradeJob?.targetVersion || '-'}</div>
        ),
      },
      {
        label: 'Completed',
        value: `${clusterUpgradeJob?.completedSteps || '0'} of ${
          Object.keys(ClusterUpgradeSteps).length
        } steps`,
      },
      {
        label: 'Started',
        value: formatDate(clusterUpgradeJob?.startTime) || '-',
      },
      {
        label: clusterUpgradeJob?.phase === 'Completed' && 'Completed',
        value: clusterUpgradeJob?.phase === 'Completed' && completionTime,
      },
      // {
      //   label: clusterUpgradeJob?.phase === 'Completed' ? 'Completed' : 'Expected Completion',
      //   value: clusterUpgradeJob?.phase === 'Completed' ? completionTime : expectedCompletionTime,
      // },
    ],
  }
  const isTimeWithinTwoDays = useMemo(
    () => isTimeExpired(clusterUpgradeJob?.status?.completionTime),
    [clusterUpgradeJob?.status?.completionTime],
  )
  const isCompletedAndExpired = clusterUpgradeJob?.phase === 'Completed' && !isTimeWithinTwoDays
  const shouldShowStatusBar =
    clusterUpgradeJob &&
    !isCompletedAndExpired &&
    !((k8sPluginGlobalPerfs?.hideUpgradeSuccessBanner as Array<string>) || [])?.includes(
      cluster?.metadata?.name,
    )

  const handleRetryClick = () => {
    history.push(
      routeManager.routes.kubernetes.infrastructure.clusterUpgrade.path({
        cluster: cluster?.metadata?.name,
      }),
    )
  }

  return (
    <>
      <DocumentMeta
        title="Cluster Details"
        breadcrumbs
        data-testid="cluster-details-document-meta"
      />
      <Progress loading={loading} data-testid="cluster-details-progress">
        {clusters.length > 0 ? (
          <>
            <ConfirmationDialog
              open={showDeleteDialog}
              text={`Delete cluster - ${cluster?.metadata?.name} ?`}
              onCancel={() => setShowDeleteDialog(false)}
              onConfirm={deleteCluster}
              loading={deletingKaapiCluster}
              data-testid="cluster-details-delete-dialog"
            />

            {shouldShowStatusBar && (
              <StatusBar
                statusDetails={statusDetails}
                onSeeDetailsClick={() =>
                  history.push(
                    routeManager.routes.kubernetes.infrastructure.upgradeStatus.path({
                      cluster: cluster?.metadata?.name,
                    }),
                  )
                }
                onClose={() => {
                  updatePrefs({
                    hideUpgradeSuccessBanner: [
                      ...((k8sPluginGlobalPerfs?.hideUpgradeSuccessBanner as Array<string>) || []),
                      cluster.metadata.name,
                    ],
                  })
                }}
                onRetryClick={handleRetryClick}
                data-testid="cluster-details-status-bar"
              />
            )}
            {unhealthyMachines.length > 0 && (
              <Alert
                variant="error"
                title={getUnhealthyMachinesBannerTitle(unhealthyMachines)}
                message={`${unhealthyMachines[0]?.name}: ${(
                  unhealthyMachines[0]?.machineHealth?.message || ''
                ).slice(0, 240)} - see the Nodes tab for details.`}
                className={classes.errorBanner}
                data-testid="cluster-details-scaleup-error-banner"
              />
            )}
            {isUnhealthy(clusterHealth) && (
              <Alert
                variant="error"
                title="Cluster is reporting an error"
                message={`${clusterHealth?.reason ? `${clusterHealth.reason}: ` : ''}${(
                  clusterHealth?.message || ''
                ).slice(0, 240)}`}
                className={classes.errorBanner}
                data-testid="cluster-details-cluster-health-banner"
              />
            )}
            <ClusterUpgradeModal
              onClose={() =>
                history.push(
                  routeManager.routes.kubernetes.infrastructure.cluster.path({
                    cluster: cluster?.metadata?.name,
                    tab: IClusterDetailTabs.OVERVIEW,
                  }),
                )
              }
              data-testid="cluster-details-upgrade-modal"
            />
            <div
              className={classes.customGridContainer}
              data-testid="cluster-details-custom-grid-container"
            >
              <div>
                <ClusterOverview
                  deleteClusterAction={() => setShowDeleteDialog(true)}
                  data-testid="cluster-details-cluster-overview"
                />
              </div>
              <div>
                <div>
                  <Text
                    variant="caption1"
                    className={classes.headerText}
                    data-testid="cluster-details-resource-utilization-text"
                  >
                    RESOURCE UTILIZATION
                  </Text>
                </div>
                <div className={classes.divider} data-testid="cluster-details-divider" />
                <ResourceUtilizationCard data-testid="cluster-details-resource-utilization-card" />
              </div>
            </div>
          </>
        ) : (
          <Text variant="body1" data-testid="cluster-details-no-clusters-found-text">
            No clusters found
          </Text>
        )}
      </Progress>
    </>
  )
}

export default ClusterDetails

const useStyles = makeStyles((theme: Theme) => ({
  customGridContainer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 370px) 1fr',
    gridGap: '24px',
    '& > div': { minWidth: 0 },
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  divider: {
    border: '0',
    height: '1px',
    margin: '16px 0px',
    background: '#e6e6ea',
  },
  errorBanner: {
    marginBottom: theme.spacing(2),
  },
  currentVersion: {
    display: 'flex',
    alignItems: 'center',
  },
}))
